import { createFocusTrap } from "focus-trap"
import { Router } from "next/router"
import React, { useEffect, useRef, useState } from "react"
import { useListener } from "react-bus"
import { useSettings } from "src/layout/page/settingsContext"
import { InternalLink, SanityRichText } from "src/sanity/types"

import { Link } from "src/components/link/Link"

import cn from "classnames"

import s from "./InvestmentOverlay.module.scss"
import { Container } from "src/layout/container/Container"
import Icon, { IconType } from "../icon/Icon"
import buildLink from "src/sanity/buildLink"
import FinalStep from "./investment-steps/FinalStep"
import FirstMiddleStep from "./investment-steps/FirstMiddleStep"
import SecondMiddleStep from "./investment-steps/SecondMiddleStep"
import ThirdMiddleStep from "./investment-steps/ThirdMiddleStep"
import { StartInvestmentTab, MonthlyInvestmentTab, RadioLabel } from "src/modules/investment-module/InvestmentModule"
import InfoModal from "../info-modal/InfoModal"

type InvestmentOverlayProps = {
    rate:number
    period: number
    maxYears: number
    startInvestmentTabs: StartInvestmentTab[]
    monthlyInvestmentTabs: MonthlyInvestmentTab[]
    radioLabels: RadioLabel[]
    investmentInputStartLabel: string
    investmentInputMonthlyLabel: string
    investmentInputStartPlaceholder: string
    investmentInputMonthlyPlaceholder: string
    initialStepHeading: string
    initialStepDescription:string
    initialStepButtonLabel: string
    firstMiddleStepNumber: string
    firstMiddleStepHeading: string
    firstMiddleStepDescription: string
    secondMiddleStepNumber: string
    secondMiddleStepHeading: string
    secondMiddleStepDescription: string
    thirdMiddleStepNumber: string
    thirdMiddleStepHeading: string
    thirdMiddleStepDescription: string
    middleStepPreviousButtonLabel: string
    middleStepNextButtonLabel: string
    thirdMiddleStepSeeResultButtonLabel: string
    finalStepTitle: string
    finalStepCalculationDescription: string
    finalStepButtonLabel: string
    finalStepModalButtonText: string
    finalStepStartMoneyAmountSummary: string
    finalStepMonthlyMoneyAmountSummary: string
    finalStepRadioInputSummary: string
    currency: string
    zeroInterestRateLabel: string
    estimatedRefundLabel: string
    tooltipTitle: string
    howisThisCalculatedModalTitle?: string
    howisThisCalculatedModalContent: SanityRichText
    stepValue: number
    handleNextStep: () => void
    handlePreviousStep: () => void
    setStepValue: (newStep: number) => void
    riskInfo: string
}

export default function InvestmentOverlay(props: InvestmentOverlayProps) {

    const { stepValue, setStepValue, handleNextStep, handlePreviousStep } = props

    const [open, setOpen] = useState(false)
    const { translations: t, segment, lang } = useSettings()
    Router.events.on("routeChangeStart", () =>  setOpen(false))
    Router.events.on("routeChangeComplete", () =>  setOpen(false))
    Router.events.on("routeChangeError", () =>  setOpen(false))

    const {
        radioLabels,
        howisThisCalculatedModalTitle,
        howisThisCalculatedModalContent,
    } = props

    const [radioCheckedValue, setRadioCheckedValue] = useState(
        radioLabels[0].radioLabel,
    )


    const [startInputText, setStartInputText] = useState("")
    const [monthlyInputText, setMonthlyInputText] = useState("0")

    const clearHandler = () => {
        setStartInputText("")
        setMonthlyInputText("0")
        setStepValue(0)
        setOpen(false)
    }


    const homeLink: InternalLink = {
        id: "",
        title: "",
        linkType: "internal",
        type: "link",
        url: {
            type: "frontpage",
            lang,
            segment,
            slug: "",
        },
    }

    const ref = useRef<HTMLDivElement>()

    useListener("investmentOverlay", () => {
        setOpen(true)
    })

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = "auto"
        }
    }, [open])

    useEffect(() => {
        if (open) {
            const trap = createFocusTrap(ref.current, {
                onDeactivate: () => setOpen(false),
            })

            trap.activate()

            return () => {
                trap.deactivate()
            }
        }
    }, [open])


    return (
        <div className={cn(s["investment-overlay"])} >

            <div
                ref={ref}
                style={{ opacity: open ? "1" : "0", pointerEvents: open ? "all" : "none" }}
                className={cn(s["investment-overlay__popup"], { [s["investment-overlay__popup--closed"]]:!open })}
            >
                <Container className={s["investment-overlay__container"]}>
                    <button
                        onClick={() => clearHandler()}
                        className={cn(s["investment-overlay__close"], "button", "button--close")}
                    >
                        <Icon type={IconType.X} />
                        <span className="visually-hidden">{t?.languages?.close}</span>
                    </button>
                    <Link href={buildLink(homeLink)} className={cn(s["investment-overlay__logo"])}>
                        <Icon className={s["investment-overlay__logo__svg"]} type={IconType.LOGO} />
                        <span className="visually-hidden">
                            Lunar {t?.header?.home.toLocaleLowerCase()}
                        </span>
                    </Link>
                    <div>

                        {stepValue === 0 && (<FirstMiddleStep
                            sharedClasses={{ stepContainer: s["investment-overlay__step"], buttonContainer: s["investment-overlay__button-container"], textContainer: s["investment-overlay__text-container"], description: s["investment-overlay__description"] }}
                            onNextStep={handleNextStep}
                            onPreviousStep={handlePreviousStep}
                            inputText={startInputText}
                            setInputText={setStartInputText}
                            {...props}
                        />)}

                        {stepValue === 1 && (<SecondMiddleStep
                            sharedClasses={{ stepContainer: s["investment-overlay__step"], buttonContainer: s["investment-overlay__button-container"], textContainer: s["investment-overlay__text-container"], description: s["investment-overlay__description"] }}
                            onNextStep={handleNextStep}
                            onPreviousStep={handlePreviousStep}
                            inputText={monthlyInputText}
                            setInputText={setMonthlyInputText}
                            {...props}
                        />)}

                        {stepValue === 2 && (<ThirdMiddleStep
                            sharedClasses={{ stepContainer: s["investment-overlay__step"], buttonContainer: s["investment-overlay__button-container"], textContainer: s["investment-overlay__text-container"], description: s["investment-overlay__description"] }}
                            onNextStep={handleNextStep}
                            onPreviousStep={handlePreviousStep}
                            radioCheckedValue={radioCheckedValue}
                            setRadioCheckedValue={setRadioCheckedValue}
                            {...props}
                        />)}

                        {stepValue === 3 && (<FinalStep
                            sharedClasses={{ stepContainer: s["investment-overlay__step"] }}
                            radioCheckedValue={radioCheckedValue}
                            setRadioCheckedValue={setRadioCheckedValue}
                            startInputText={startInputText}
                            monthlyInputText={monthlyInputText}
                            setInputText={setStartInputText}
                            setMonthlyInputText={setMonthlyInputText}
                            {...props}
                        />)}

                    </div>
                    <InfoModal modalVersion event="investmentCalculation" body={howisThisCalculatedModalContent} title={howisThisCalculatedModalTitle} />
                </Container>
            </div>
        </div>
    )
}
